import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  Package,
  Plus,
  Link2,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Settings,
  BarChart3
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface PackageStats {
  package_id: string
  slug: string
  name: string
  latest_version: string
  review_status: string
  connection_count: number
  view_count: number
}

export default function PublisherDashboardPage() {
  const { publisher, token } = usePublisherAuth()
  const [packages, setPackages] = useState<PackageStats[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/publisher/me/packages`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
        if (!res.ok) {
          setPackages([])
          return
        }
        const data = await res.json()
        if (Array.isArray(data)) {
          setPackages(data.map((pkg: any) => ({
            package_id: pkg.package_id || '',
            slug: pkg.slug || '',
            name: pkg.name || '',
            latest_version: pkg.latest_version || '0.0.0',
            review_status: pkg.review_status || 'draft',
            connection_count: pkg.connection_count || 0,
            view_count: pkg.view_count || 0,
          })))
        } else {
          setPackages([])
        }
      } catch {
        setPackages([])
      } finally {
        setLoading(false)
      }
    }
    fetchPackages()
  }, [token])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        )
      case 'submitted':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3 mr-1" />
            In Review
          </span>
        )
      case 'changes_requested':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Changes Requested
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Draft
          </span>
        )
    }
  }

  const totalConnections = packages.reduce((sum, pkg) => sum + pkg.connection_count, 0)
  const totalViews = packages.reduce((sum, pkg) => sum + pkg.view_count, 0)

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {publisher?.display_name}
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your MCP skills and track their performance
          </p>
        </div>
        <Link to="/publisher/packages/new" className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          New Package
        </Link>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Packages</p>
              <p className="text-2xl font-bold text-gray-900">{packages.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Link2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Connections</p>
              <p className="text-2xl font-bold text-gray-900">{totalConnections.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Views</p>
              <p className="text-2xl font-bold text-gray-900">{totalViews.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Verified</p>
              <p className="text-2xl font-bold text-gray-900">
                {publisher?.verified ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Packages list */}
      <div className="card">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your Packages</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-500">Loading packages...</p>
          </div>
        ) : packages.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No packages yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first MCP skill package to get started.
            </p>
            <Link to="/publisher/packages/new" className="btn btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Package
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {packages.map((pkg) => (
              <div key={pkg.package_id} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-base font-medium text-gray-900 truncate">
                        {pkg.name}
                      </h3>
                      {getStatusBadge(pkg.review_status)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {pkg.slug} &middot; v{pkg.latest_version}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Link2 className="w-4 h-4 mr-1" />
                        {pkg.connection_count.toLocaleString()} connections
                      </span>
                      <span className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {pkg.view_count.toLocaleString()} views
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/skills/${pkg.slug}`}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="View public page"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </Link>
                    <Link
                      to={`/publisher/packages/${pkg.package_id}/analytics`}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Analytics"
                    >
                      <BarChart3 className="w-5 h-5" />
                    </Link>
                    <Link
                      to={`/publisher/packages/${pkg.package_id}/settings`}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Settings"
                    >
                      <Settings className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="font-medium text-gray-900 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <Link
              to="/docs/publishing"
              className="block p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Publishing Guide</p>
              <p className="text-sm text-gray-500">Learn how to publish MCP skills</p>
            </Link>
            <Link
              to="/docs/best-practices"
              className="block p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Best Practices</p>
              <p className="text-sm text-gray-500">Tips for building great skills</p>
            </Link>
          </div>
        </div>
        <div className="card p-5">
          <h3 className="font-medium text-gray-900 mb-3">Publisher Profile</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Slug</span>
              <span className="text-sm font-mono text-gray-900">{publisher?.slug}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Type</span>
              <span className="text-sm text-gray-900 capitalize">{publisher?.type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`text-sm font-medium ${publisher?.verified ? 'text-green-600' : 'text-gray-600'}`}>
                {publisher?.verified ? 'Verified' : 'Unverified'}
              </span>
            </div>
            <Link
              to="/publisher/settings"
              className="btn btn-secondary w-full mt-2"
            >
              Edit Profile
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
